import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Container,
  Row,
  Col,
  Form,
  Spinner,
  InputGroup,
  Alert,
  Modal,
  Badge,
} from "react-bootstrap";
import {
  FaCheck,
  FaSearch,
  FaTimes,
  FaTrash,
  FaThumbsUp,
  FaThumbsDown,
  FaSync,
  FaCalendarCheck,
  FaTruck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { translateBackendMessage } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { userAPI, postalAreaAPI } from "../../api/apiSystem";
import ClientChatBox from "../chat/ClientChatBox";
import "./ClientTable.css";
import { formatNumber } from "../../utils/helpers";

// Pagination import Pagination Component
import PaginationComponent from "../PaginationComponent";

// SearchFilter import useSearchFilter Component
import { useSearchFilter } from "../useSearchFilter";
import { FaArrowRightLong, FaRegCircleCheck } from "react-icons/fa6";
import { PiNoteBold } from "react-icons/pi";

export default function ClientTable() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // 🔹 Active Tab state
  const [activeTab, setActiveTab] = useState("booking");

  // 🔹 API data for each tab
  const [bookingData, setBookingData] = useState([]);
  const [furnitureData, setFurnitureData] = useState([]);
  const [carData, setCarData] = useState([]);
  const [countData, setCountData] = useState([]);

  const [loading, setLoading] = useState(false);

  // 🔹 Delete Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState({
    show: false,
    message: "",
    variant: "",
  });
  const [deleteReason, setDeleteReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const [ordersUnreadCounts, setOrdersUnreadCounts] = useState({});

  // SearchFilter Search State and Filter data
  const [searchTerm, setSearchTerm] = useState("");

  // Filtered data for each tab - Search by order_id instead of booking_id
  const filteredBookingData = useSearchFilter(bookingData, searchTerm, [
    "order_id",
  ]);
  const filteredFurnitureData = useSearchFilter(furnitureData, searchTerm, [
    "order_id",
  ]);
  const filteredCarData = useSearchFilter(carData, searchTerm, ["order_id"]);

  // Pagination State for each tab
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [furniturePagination, setFurniturePagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [carPagination, setCarPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // 🟠 When page loads or tab changes
  useEffect(() => {
    if (activeTab === "booking") {
      fetchBookings();
    } else if (activeTab === "furniture") {
      fetchFurnitureBookings();
    } else if (activeTab === "car") {
      fetchCarBookings();
    }
    fetchCounts();
  }, [activeTab]);

  useEffect(() => {
    if (!countData) return;

    const availableTabs = ["booking", "furniture", "car"].filter((tab) => {
      if (tab === "booking") return (countData.bookings || 0) > 0;
      if (tab === "furniture") return (countData.furniture_transports || 0) > 0;
      if (tab === "car") return (countData.car_transports || 0) > 0;
      return false;
    });

    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [countData, activeTab]);

  // ✅ Function to fetch general bookings data
  const fetchBookings = async (page = 1) => {
    console.log("🚀 fetchBookings() called");
    setLoading(true);
    try {
      const response = await userAPI.bookings.index({
        page,
        per_page: pagination.per_page,
      });
      console.log("📦 API response:", response);

      if (response.success) {
        const filteredData = response.data.data || response.data;
        setBookingData(filteredData);
        console.log("✅ bookingData:", filteredData);

        await fetchUnreadCountsForOrders(filteredData);

        setPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );
      } else {
        console.error("Failed to load data:", response.message);
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadBookingsFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Error loading bookings:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.connectionFailed"),
        confirmButtonColor: "#ff7b00",
      });
    }
    setLoading(false);
  };

  const fetchUnreadCountsForOrders = async (ordersData) => {
    const unreadCounts = {};

    for (const order of ordersData) {
      try {
        const transportId = order.transport?.id || order.transport_id;
        const orderId = order.id;

        if (!transportId || !orderId) continue;

        console.log("📊 Fetching unread count for order:", orderId);

        const response = await userAPI.transportOrderChat.getMessages(
          transportId,
          orderId,
        );

        if (response.success) {
          let statistics = { unread_count: 0 };

          if (response.data && response.data.statistics) {
            statistics = response.data.statistics;
          } else if (
            response.data &&
            response.data.data &&
            response.data.data.statistics
          ) {
            statistics = response.data.data.statistics;
          }

          unreadCounts[orderId] = statistics.unread_count || 0;
          console.log(
            `📊 Unread count for order ${orderId}:`,
            statistics.unread_count,
          );
        }
      } catch (err) {
        console.error(
          `❌ Error fetching unread count for order ${order.id}:`,
          err,
        );
        unreadCounts[order.id] = 0;
      }
    }

    setOrdersUnreadCounts(unreadCounts);
  };

  // دالة لتحديث عدد الرسائل غير المقروءة لطلب معين
  const updateOrderUnreadCount = (orderId, newCount) => {
    setOrdersUnreadCounts((prev) => ({
      ...prev,
      [orderId]: newCount,
    }));
  };

  // ✅ Function to repeat transport booking
  const handleRepeatTransport = async (transportData) => {
    console.log("🔄 Repeating transport:", transportData);

    try {
      // تحضير البيانات للانتقال إلى نموذج النقل
      const repeatData = {
        // بيانات النقل السابقة كاملة
        previousTransport: transportData,
        // نوع العملية
        isRepeatTransport: true,
        // نوع النقل
        transportType: getTransportType(transportData.transport_type),
        // بيانات المواقع والتواريخ
        locations: extractTransportLocations(transportData),
        // بيانات العناصر
        itemsData: extractTransportItems(transportData),
        // البيانات الإضافية
        additionalData: extractTransportAdditionalData(transportData),
      };

      console.log("✅ Prepared repeat transport data:", repeatData);

      // الانتقال إلى صفحة نموذج النقل مع البيانات
      navigate("/transport-form", { state: repeatData });
    } catch (error) {
      console.error("❌ Error in repeat transport:", error);

      // استخدام البيانات الأساسية في حالة الخطأ
      const fallbackData = {
        previousTransport: transportData,
        isRepeatTransport: true,
        transportType: getTransportType(transportData.transport_type),
        locations: extractBasicLocations(transportData),
        itemsData: [createEmptyItem()],
        additionalData: {
          pickupStartDateTime: "",
          pickupEndDateTime: "",
          deliveryStartDateTime: "",
          deliveryEndDateTime: "",
          fragile: "",
          fragileDetails: "",
          specialHandling: "",
          handlingDetails: "",
          helpCarry: "0",
          beladungFloor: "",
          entladungFloor: "",
          liftBeladung: false,
          liftEntladung: false,
          driving: [],
          description: "",
          images: [],
          deliveryAs: "",
        },
      };

      navigate("/transport-form", { state: fallbackData });
    }
  };

  // دالة لتحديد نوع النقل
  const getTransportType = (transportType) => {
    const typeMap = {
      moving: { name: "moving", label: "Umzüge" },
      furniture: { name: "furniture", label: "Möbel & Sonstiges" },
      car: { name: "car", label: "Autos" },
      palette: { name: "palette", label: "Palette" },
      document: { name: "document", label: "Dokumente" },
      bicycle: { name: "bicycle", label: "Fahrräder" },
      pakete: { name: "pakete", label: "Pakete" },
      tires: { name: "tires", label: "Reifen" },
      refrigerator: { name: "refrigerator", label: "Kühlschrank" },
      electrical: { name: "electrical", label: "Elektrogeräte" },
      motorcycle: { name: "motorcycle", label: "Motorräder" },
      "building materials": {
        name: "building materials",
        label: "Baumaterialien",
      },
      "animal transport": { name: "animal transport", label: "Tiertransport" },
      other: { name: "other", label: "Sonstige" },
    };

    return (
      typeMap[transportType] || { name: transportType, label: transportType }
    );
  };

  // دالة لاستخراج بيانات المواقع
  const extractTransportLocations = (transportData) => {
    return {
      booking_from: transportData.booking_from,
      booking_to: transportData.booking_to,
      from_lat_long: transportData.booking_from_lat_long,
      to_lat_long: transportData.booking_to_lat_long,
      price_offer: transportData.price_offer,
      is_price_negotiable: transportData.is_price_negotiable,
      payment_term: transportData.payment_term,
    };
  };

  // دالة لاستخراج بيانات العناصر
  // دالة محسنة لاستخراج بيانات العناصر
  const extractTransportItems = (transportData) => {
    console.log("📋 Extracting items from:", transportData);

    const items = [];

    // معالجة البيانات بناءً على هيكل API
    if (transportData.item_type && Array.isArray(transportData.item_type)) {
      transportData.item_type.forEach((itemType, index) => {
        const item = {
          item_type: itemType || "",
          num_items: transportData.num_items?.[index] || "",
          item_weight:
            transportData.item_weight?.[index] ||
            transportData.total_weight ||
            "",
          total_weight:
            transportData.total_weight ||
            transportData.item_weight?.[index] ||
            "",
          board_number: transportData.board_number?.[index] || "",
          making_year: transportData.making_year?.[index] || "",
          content_pakage: transportData.content_pakage?.[index] || "",
        };

        // إضافة الأبعاد إذا كانت متوفرة
        if (transportData.dimensions && transportData.dimensions[index]) {
          const dim = transportData.dimensions[index].split("x");
          if (dim.length === 3) {
            item.length = dim[0];
            item.width = dim[1];
            item.height = dim[2];
          }
        } else {
          // قيم افتراضية للأبعاد
          item.length = "";
          item.width = "";
          item.height = "";
        }

        // تنظيف البيانات - إزالة القيم null/undefined
        Object.keys(item).forEach((key) => {
          if (item[key] === null || item[key] === undefined) {
            item[key] = "";
          }
        });

        items.push(item);
      });
    } else if (transportData.item_type) {
      // إذا كانت البيانات ليست مصفوفة
      const item = {
        item_type: transportData.item_type || "",
        num_items: transportData.num_items || "",
        item_weight:
          transportData.item_weight || transportData.total_weight || "",
        total_weight: transportData.total_weight || "",
        board_number: transportData.board_number || "",
        making_year: transportData.making_year || "",
        content_pakage: transportData.content_pakage || "",
        length: "",
        width: "",
        height: "",
      };

      // تنظيف البيانات
      Object.keys(item).forEach((key) => {
        if (item[key] === null || item[key] === undefined) {
          item[key] = "";
        }
      });

      items.push(item);
    }

    console.log("✅ Extracted items:", items);
    return items.length > 0 ? items : [createEmptyItem()];
  };

  // دالة لإنشاء عنصر فارغ
  const createEmptyItem = () => {
    return {
      item_type: "",
      num_items: "",
      item_weight: "",
      total_weight: "",
      board_number: "",
      making_year: "",
      content_pakage: "",
      length: "",
      width: "",
      height: "",
    };
  };

  // دالة محسنة لاستخراج البيانات الإضافية
  const extractTransportAdditionalData = (transportData) => {
    console.log("📦 Extracting additional data from:", transportData);

    const additionalData = {
      fragile: transportData.item_breakable || "",
      fragileDetails: transportData.item_breakable_details || "",
      specialHandling: transportData.special_treatment || "",
      handlingDetails: transportData.special_treatment_details || "",
      helpCarry: transportData.load_helping || "0",
      beladungFloor: transportData.loading_floor || "",
      entladungFloor: transportData.unloading_floor || "",
      liftBeladung: transportData.loading_elevator == 1,
      liftEntladung: transportData.unloading_elevator == 1,
      driving: Array.isArray(transportData.driving)
        ? transportData.driving
        : [],
      description: transportData.description || "",
      images: handleImages(transportData.images),
      deliveryAs: transportData.delivery_as || "",
    };

    console.log("✅ Extracted additional data:", additionalData);
    return additionalData;
  };

  // دالة خاصة لمعالجة الصور
  // دالة محسنة لمعالجة الصور
  const handleImages = (imagesData) => {
    console.log("🖼️ Handling images data:", imagesData);

    if (!imagesData) {
      console.log("❌ No images data");
      return [];
    }

    // إذا كانت الصور عبارة عن مصفوفة من URLs (نصوص)
    if (Array.isArray(imagesData) && imagesData.length > 0) {
      // تصفية URLs الصالحة فقط
      const validImageUrls = imagesData.filter(
        (url) =>
          url &&
          typeof url === "string" &&
          (url.startsWith("http") || url.startsWith("/")),
      );

      console.log("✅ Valid image URLs:", validImageUrls);
      return validImageUrls;
    }

    console.log("❌ Invalid images format");
    return [];
  };

  // دالة احتياطية للمواقع الأساسية
  const extractBasicLocations = (transportData) => {
    return {
      booking_from: transportData.booking_from,
      booking_to: transportData.booking_to,
      from_lat_long: transportData.booking_from_lat_long,
      to_lat_long: transportData.booking_to_lat_long,
      price_offer: transportData.price_offer,
      is_price_negotiable: transportData.is_price_negotiable,
      payment_term: transportData.payment_term,
    };
  };
  // في ملف ClientTable.jsx - أضف هذه الدالة
  // في ملف ClientTable.jsx - تحديث دالة handleRepeatBooking
  const handleRepeatBooking = async (bookingData) => {
    console.log("🔄 Repeating booking:", bookingData);

    try {
      // استخراج بيانات المركبة من API
      const vehicleInfo = await extractVehicleInfo(bookingData);

      const originalSubTotal =
        bookingData.original_sub_total || bookingData.sub_total || bookingData.price;
      const originalTotal =
        bookingData.original_total || bookingData.total || bookingData.price;

      // تحضير البيانات للانتقال إلى نموذج الحجز
      const repeatData = {
        // بيانات المركبة من API
        selectedVehicleInfo: vehicleInfo,
        // بيانات الطلب السابق كاملة
        previousBooking: bookingData,
        // نوع العملية
        isRepeatBooking: true,
        // بيانات النموذج من الطلب السابق
        formData: extractFormData(bookingData),
        // إضافة السعر من الطلب السابق
        previousPrice: {
          sub_total: originalSubTotal,
          total: originalTotal,
          calculatedPrice: bookingData.calculatedPrice,
        },
      };

      console.log("✅ Prepared repeat data with price:", repeatData);

      // الانتقال إلى صفحة الحجز مع البيانات
      navigate("/bookings/steps", { state: repeatData });
    } catch (error) {
      console.error("❌ Error in repeat booking:", error);

      // استخدام البيانات الاحتياطية في حالة الخطأ
      const fallbackData = {
        selectedVehicleInfo: getFallbackVehicleData(
          bookingData.vehicle_id,
          bookingData.vehicle_name,
        ),
        previousBooking: bookingData,
        isRepeatBooking: true,
        formData: extractFormData(bookingData),
        previousPrice: {
          sub_total:
            bookingData.original_sub_total || bookingData.sub_total || bookingData.price,
          total: bookingData.original_total || bookingData.total || bookingData.price,
        },
      };

      navigate("/bookings/steps", { state: fallbackData });
    }
  };

  // دالة لاستخراج بيانات المركبة
  const extractVehicleInfo = async (bookingData) => {
    console.log("🚗 Extracting vehicle info from:", bookingData);

    // إذا كان هناك بيانات مركبة كاملة
    if (bookingData.vehicle) {
      return bookingData.vehicle;
    }

    try {
      // جلب بيانات جميع السيارات من API
      const response = await postalAreaAPI.index();
      console.log("📡 Vehicles API response:", response);

      if (response.success && response.data) {
        const allVehicles = response.data.data;

        // البحث عن السيارة المناسبة بناءً على vehicle_id
        const matchedVehicle = allVehicles.find(
          (vehicle) => vehicle.id === bookingData.vehicle_id,
        );

        if (matchedVehicle) {
          console.log("✅ Found matching vehicle:", matchedVehicle);

          const vehicleInfo = {
            id: matchedVehicle.id,
            name: matchedVehicle.name,
            image: matchedVehicle.image,
            description: matchedVehicle.description,
            description2: matchedVehicle.description2,
            price: matchedVehicle.price,
            size: matchedVehicle.size,
            width: matchedVehicle.width,
            height: matchedVehicle.height,
            length: matchedVehicle.length,
            weight: matchedVehicle.weight,
            max_weight: matchedVehicle.weight, // استخدام weight كـ max_weight
            amount: matchedVehicle.amount,
            vehicle_size: matchedVehicle.size,
            vehicle_type: matchedVehicle.name,
          };

          return vehicleInfo;
        } else {
          console.warn("⚠️ No matching vehicle found, using fallback");
          return getFallbackVehicleData(
            bookingData.vehicle_id,
            bookingData.vehicle_name,
          );
        }
      } else {
        console.error("❌ Failed to fetch vehicles from API");
        return getFallbackVehicleData(
          bookingData.vehicle_id,
          bookingData.vehicle_name,
        );
      }
    } catch (error) {
      console.error("🚨 Error fetching vehicles:", error);
      return getFallbackVehicleData(
        bookingData.vehicle_id,
        bookingData.vehicle_name,
      );
    }
  };

  // دالة احتياطية في حالة فشل API
  const getFallbackVehicleData = (
    vehicleId,
    vehicleName = "Standard Vehicle",
  ) => {
    const fallbackData = {
      1: {
        id: 1,
        name: "Caddy",
        weight: 400000,
        length: 160,
        width: 120,
        height: 120,
        max_weight: 400,
        vehicle_size: "Caddy 160×120×120 cm, Max. 400kg. 2 Paletten",
        amount: 2,
      },
      2: {
        id: 2,
        name: "Bus",
        weight: 1000000,
        length: 320,
        width: 145,
        height: 160,
        max_weight: 1000,
        vehicle_size: "Bus 320×145×160 cm, Max. 1000kg. 4 paletten",
        amount: 4,
      },
      3: {
        id: 3,
        name: "Maxi Bus",
        weight: 1200000,
        length: 420,
        width: 210,
        height: 210,
        max_weight: 1200,
        vehicle_size: "Maxi Bus 420x145x160 cm, Max. 1200 kg 8 Paletten",
        amount: 8,
      },
      4: {
        id: 4,
        name: "Planenbus",
        weight: 800000,
        length: 420,
        width: 210,
        height: 210,
        max_weight: 800,
        vehicle_size: "Planenbus 420 x 210 x 210 cm, Max. 800 kg. 8 Paletten",
        amount: 8,
        crane: "no",
      },
    };

    return (
      fallbackData[vehicleId] || {
        id: vehicleId || 4,
        name: vehicleName,
        weight: 800,
        length: 420,
        width: 210,
        height: 210,
        max_weight: 800,
        vehicle_size: "Standard Vehicle",
      }
    );
  };

  // دالة لاستخراج بيانات النموذج
  const extractFormData = (bookingData) => {
    const formData = {
      abholung: [],
      zustellung: [],
    };

    // استخراج عناوين التحميل
    if (
      bookingData.loading_postal &&
      Array.isArray(bookingData.loading_postal)
    ) {
      formData.abholung = bookingData.loading_postal.map((postal, index) => ({
        address: postal,
        companyName: bookingData.loading_company?.[index] || "",
        firstName: bookingData.loading_first_name?.[index] || "",
        lastName: bookingData.loading_last_name?.[index] || "",
        street: bookingData.loading_streat_no?.[index] || "",
        phone: bookingData.loading_phone?.[index] || "",
        reference: bookingData.loading_reffernce?.[index] || "",
      }));
    } else if (bookingData.booking_from) {
      formData.abholung = [
        {
          address: bookingData.booking_from,
          companyName: bookingData.loading_company || "",
          firstName: bookingData.loading_first_name || "",
          lastName: bookingData.loading_last_name || "",
          street: bookingData.loading_streat_no || "",
          phone: bookingData.loading_phone || "",
          reference: bookingData.loading_reffernce || "",
        },
      ];
    }

    // استخراج عناوين التسليم
    if (
      bookingData.unloading_postal &&
      Array.isArray(bookingData.unloading_postal)
    ) {
      formData.zustellung = bookingData.unloading_postal.map(
        (postal, index) => ({
          address: postal,
          companyName: bookingData.unloading_company?.[index] || "",
          firstName: bookingData.unloading_first_name?.[index] || "",
          lastName: bookingData.unloading_last_name?.[index] || "",
          street: bookingData.unloading_streat_no?.[index] || "",
          phone: bookingData.unloading_phone?.[index] || "",
          reference: bookingData.unloading_reffernce?.[index] || "",
        }),
      );
    } else if (bookingData.booking_to) {
      formData.zustellung = [
        {
          address: bookingData.booking_to,
          companyName: bookingData.unloading_company || "",
          firstName: bookingData.unloading_first_name || "",
          lastName: bookingData.unloading_last_name || "",
          street: bookingData.unloading_streat_no || "",
          phone: bookingData.unloading_phone || "",
          reference: bookingData.unloading_reffernce || "",
        },
      ];
    }

    return formData;
  };
  
  const fetchCounts = async () => {
    setLoading(true);
    try {
      const response = await userAPI.counts();

      if (response.success) {
        const countData = response.data.data || response.data;
        setCountData(countData);
      } else {
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadFurnitureFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Error loading furniture data:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.connectionFailed"),
        confirmButtonColor: "#ff7b00",
      });
    }
    setLoading(false);
  };

  // ✅ Function to fetch furniture bookings data
  const fetchFurnitureBookings = async (page = 1) => {
    console.log("🚀 fetchFurnitureBookings() called");
    setLoading(true);
    try {
      const response = await userAPI.transports.furnitures({
        page,
        per_page: furniturePagination.per_page,
      });
      console.log("📦 Furniture API response:", response);

      if (response.success) {
        const filteredData = response.data.data || response.data;
        setFurnitureData(filteredData);
        console.log("✅ furnitureData:", filteredData);

        setFurniturePagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );
      } else {
        console.error("Failed to load furniture data:", response.message);
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadFurnitureFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Error loading furniture data:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.connectionFailed"),
        confirmButtonColor: "#ff7b00",
      });
    }
    setLoading(false);
  };

  // ✅ Function to fetch car bookings data
  const fetchCarBookings = async (page = 1) => {
    console.log("🚀 fetchCarBookings() called");
    setLoading(true);
    try {
      const response = await userAPI.transports.cars({
        page,
        per_page: carPagination.per_page,
      });
      console.log("📦 Car API response:", response);

      if (response.success) {
        const filteredData = response.data.data || response.data;
        setCarData(filteredData);
        console.log("✅ carData:", filteredData);

        setCarPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );
      } else {
        console.error("Failed to load car data:", response.message);
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadCarFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Error loading car data:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.connectionFailed"),
        confirmButtonColor: "#ff7b00",
      });
    }
    setLoading(false);
  };

  const handleBookingDetails = async (row, type = "booking") => {
    let response;

    try {
      console.log(`🔄 Loading details for ${type}, Row:`, row);

      // 🔹 Get correct transport_id
      const { transportId, orderId } = getTransportAndOrderIds(row);

      // Use transportId for furniture and cars, row.booking.id or row.id for normal booking
      const detailsId =
        type === "furniture" || type === "cars" 
          ? transportId 
          : (row.booking?.id || row.id);

      console.log(
        `🎯 Using ID: ${detailsId} (transportId: ${transportId}, orderId: ${orderId})`,
      );

      if (type === "furniture" || type === "cars") {
        response = await userAPI.transports.show(detailsId);
      } else {
        response = await userAPI.bookings.show(detailsId);
      }

      console.log(`📋 API Response for ${type}:`, response);

      if (response.success) {
        if (type === "furniture" || type === "cars") {
          navigate(`/user/booking/transport_details/${detailsId}`, {
            state: {
              data: response.data,
              type: type,
              transportId: detailsId,
              orderId: orderId,
              fromPage: pagination.current_page,
              activeTab: activeTab,
            },
          });
        } else {
          navigate(`/user/booking/booking_details/${detailsId}`, {
            state: {
              data: response.data,
              type: type,
              fromPage: pagination.current_page,
              activeTab: activeTab,
            },
          });
        }
      } else {
        console.error(`❌ API Error:`, response.message);
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadDetailsFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error(`🚨 Error loading details:`, error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.tryAgainLater"),
        confirmButtonColor: "#ff7b00",
      });
    }
  };

  const getTransportAndOrderIds = (order) => {
    let transportId = null;
    let orderId = null;

    if (order.transport && order.transport.id) {
      transportId = order.transport.id;
      orderId = order.id;
    } else if (order.transport_id) {
      transportId = order.transport_id;
      orderId = order.id;
    } else if (order.id) {
      transportId = order.id;
      orderId = order.id;
    }

    return { transportId, orderId };
  };

  // ✅ Show delete modal for all
  const handleShowDeleteModal = (booking, type = "booking") => {
    setBookingToDelete({ ...booking, type });
    setDeleteReason("");
    setReasonError("");
    setDeleteAlert({ show: false, message: "", variant: "" });
    setShowDeleteModal(true);
  };

  // ✅ Confirm delete for all using SweetAlert
  const handleConfirmDelete = async () => {
    if (!bookingToDelete) return;

    if (!deleteReason.trim()) {
      setReasonError(t("clientTable.deleteModal.reasonRequired"));
      return;
    }

    try {
      setDeleting(true);
      setReasonError("");

      let response;
      const { id, type } = bookingToDelete;

      // Show loading SweetAlert
      Swal.fire({
        title: t("clientTable.deleteModal.deleting"),
        text: t("clientTable.deleteModal.pleaseWait"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      if (type === "furniture" || type === "cars") {
        // Use new endpoint for transport deletion
        response = await userAPI.transports.delete(id, {
          deleted_reason: deleteReason.trim(),
        });
      } else {
        response = await userAPI.bookings.delete(id, {
          deleted_reason: deleteReason.trim(),
        });
      }

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("clientTable.deleteModal.success"),
          text: t("clientTable.deleteModal.deletedSuccessfully"),
          confirmButtonColor: "#ff7b00",
          timer: 1500,
          showConfirmButton: false,
        });

        setTimeout(() => {
          setShowDeleteModal(false);
          setDeleteReason("");
          // Reload data based on type
          if (type === "furniture") {
            fetchFurnitureBookings(furniturePagination.current_page);
          } else if (type === "cars") {
            fetchCarBookings(carPagination.current_page);
          } else {
            fetchBookings(pagination.current_page);
          }
        }, 1500);
      } else {
        Swal.fire({
          icon: "error",
          title: t("clientTable.deleteModal.deleteFailed"),
          text: response.message || t("clientTable.deleteModal.failedToDelete"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (err) {
      console.error("Error deleting booking:", err);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.error"),
        text: translateBackendMessage(
          err?.response?.data || err,
          t("clientTable.deleteModal.deletionError"),
        ),
        confirmButtonColor: "#ff7b00",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteReason("");
    setReasonError("");
    setDeleteAlert({ show: false, message: "", variant: "" });
  };

  // SearchFilter to clear search with X btn
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Helper function to get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case "furniture":
        return filteredFurnitureData;
      case "car":
        return filteredCarData;
      default:
        return filteredBookingData;
    }
  };

  // Helper function to get current pagination
  const getCurrentPagination = () => {
    switch (activeTab) {
      case "furniture":
        return furniturePagination;
      case "car":
        return carPagination;
      default:
        return pagination;
    }
  };

  // Helper function to load next page based on active tab
  const handlePageChange = (page) => {
    switch (activeTab) {
      case "furniture":
        fetchFurnitureBookings(page);
        break;
      case "car":
        fetchCarBookings(page);
        break;
      default:
        fetchBookings(page);
    }
  };

  // Helper function to extract booking_from and booking_to from data
  const getBookingLocation = (row) => {
    // If there's a transport object
    if (row.transport) {
      return {
        from: row.transport.booking_from,
        to: row.transport.booking_to,
        distance: row.transport.distance,
        transport_id: row.transport.id,
      };
    }

    // If data is directly in the row
    return {
      from: row.booking_from,
      to: row.booking_to,
      distance: row.distance,
      transport_id: row.transport_id,
    };
  };

  // Function to determine if accept/reject buttons should be shown
  const showAcceptRejectButtons = (row) => {
    const isTransportTab =
      activeTab === "furniture" || activeTab === "car";
    const isPending = row.status === "pending";

    // Use new function to check for required data
    const { transportId, orderId } = getTransportAndOrderIds(row);
    const hasRequiredData = transportId && orderId;

    console.log(`🔍 showAcceptRejectButtons for row ${row.id}:`, {
      isTransportTab,
      isPending,
      transportId,
      orderId,
      hasRequiredData,
    });

    return isTransportTab && isPending && hasRequiredData;
  };

  // ✅ Function to determine if delete button should be shown
  const showDeleteButton = (row) => {
    const isTransportTab =
      activeTab === "furniture" || activeTab === "car";
    const isBookingTab = activeTab === "booking";
    const deletableStatuses = ["pending", "approved"];
    const acceptedOrderStatuses = ["assigned", "received", "completed"];
    const hasAcceptedOrder =
      Number(row.order_status) === 1 ||
      acceptedOrderStatuses.includes(row.order?.status);

    // Can delete until a driver order is accepted.
    if (isTransportTab && deletableStatuses.includes(row.status) && !hasAcceptedOrder) {
      return true;
    }

    if (isBookingTab && deletableStatuses.includes(row.status) && !hasAcceptedOrder) {
      return true;
    }

    return false;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      cancelled: { variant: "danger", text: t("cancelled") },
      rejected: { variant: "danger", text: t("rejected") },
      assigned: { variant: "info", text: t("assigned") },
      received: { variant: "secondary", text: t("received") },
      completed: { variant: "success", text: t("completed") },
      pending: { variant: "warning", text: t("pending") },
      approved: { variant: "success", text: t("approved") },
    };

    const config = statusConfig[status] || { variant: "light", text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  return (
    <>
      <NavbarMenu />
      <Container fluid className="py-4">
        
        <h4 className="page-title mb-4" style={{ color: "#fff" }}>
          {t("clientTable.title")}
        </h4>

        {/* Dark Card Wrapper */}
        <div className="p-4">
          
          {/* 🔹 Tabs */}
          <div className="dark-tabs-container">
            {["booking", "furniture", "car"]
              .filter((tab) => {
                if (!countData) return true; 

                if (tab === "booking") return (countData.bookings || 0) > 0;
                if (tab === "furniture") return (countData.furniture_transports || 0) > 0;
                if (tab === "car") return (countData.car_transports || 0) > 0;

                return true;
              })
              .map((tab) => (
                <Button
                  key={tab}
                  size="sm"
                  onClick={() => setActiveTab(tab)}
                  className={`dark-tab-btn ${activeTab === tab ? "active" : ""}`}
                >
                  {t(`clientTable.tabs.${tab}`)}
                  {countData && (
                    <span className="ms-2 badge bg-success rounded-circle">
                      {tab === "booking"
                        ? countData.bookings
                        : tab === "furniture"
                        ? countData.furniture_transports
                        : countData.car_transports}
                    </span>
                  )}
                </Button>
              ))}
          </div>

          {/* 🔹 Quick Actions */}
          <Container>
            <div className="quick-action d-flex align-items-center justify-content-center flex-wrap gap-5 mb-4">
              <div>
                <Button
                  className="quick-action-btn transport-btn"
                  onClick={() => {
                    localStorage.removeItem("bookingData");
                    navigate("/bookings");
                  }}
                >
                  <div className="btn-content">
                    <div className="btn-icon-wrapper">
                      <FaTruck className="btn-icon" />
                    </div>
                    <div className="btn-text-wrapper">
                      <span className="btn-title">{t("clientTable.quickActions.request_quote")}</span>
                      <span className="btn-subtitle">{t("clientTable.quickActions.transport_sub")}</span>
                    </div>
                  </div>
                  <div className="btn-arrow">
                    <FaArrowRightLong className="arrow-icon" />
                  </div>
                </Button>

                <p className="hero-data-note justify-content-center">
                  <FaRegCircleCheck /> {" "}
                  {t(
                    "clientTable.quickActions.transportNote",
                    "Festen Preis sehen und jetzt buchen"
                  )}
                </p>
              </div>

              <div className="booking-div">
                <Button
                  className="quick-action-btn booking-btn"
                  onClick={() => navigate("/transport")}
                >
                  <div className="btn-content">
                    <div className="btn-icon-wrapper">
                      <PiNoteBold className="btn-icon" />
                    </div>
                    <div className="btn-text-wrapper">
                      <span className="btn-title">{t("clientTable.quickActions.direct_booking")}</span>
                      <span className="btn-subtitle">{t("clientTable.quickActions.booking_sub")}</span>
                    </div>
                  </div>
                  <div className="btn-arrow">
                    <FaArrowRightLong className="arrow-icon" />
                  </div>
                </Button>
                <p className="hero-data-note justify-content-center">
                  <FaRegCircleCheck /> {" "}
                  {t(
                    "clientTable.quickActions.bookingNote",
                    "Unverbindlich anfragen - Schnell & Kostenlos"
                  )}
                </p>
              </div>
            </div>
          </Container>

          {/* Search + Show Entries */}
          <div className="dark-table-controls mb-3 mt-4">
            <div className="entries-control">
              <span>{t("clientTable.show")}</span>
              <Form.Select
                size="sm"
                className="dark-search-input"
                style={{ width: "80px" }}
              >
                <option>10</option>
                <option>25</option>
                <option>50</option>
              </Form.Select>
              <span>{t("clientTable.entries")}</span>
            </div>

            {/* SearchFilter Search Bar */}
            <InputGroup
              className="dark-input-group"
              style={{ maxWidth: "350px" }}
            >
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t("clientTable.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dark-search-input"
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={clearSearch}
                  title={t("clientTable.clearSearch")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Table */}
          <div className="table-responsive">
            {loading ? (
              <div className="dark-loading py-5">
                <Spinner animation="border" />
                <p className="mt-3" style={{ color: "#c5c5c5" }}>
                  {t("clientTable.loading") || "Loading..."}
                </p>
              </div>
            ) : (
              <Table
                hover
                responsive
                className="align-middle text-center orders-table"
              >
                <thead>
                  <tr className="fw-bold">
                    <th>#</th>
                    <th>{t("clientTable.tableHeaders.orderId")}</th>
                    <th>{t("clientTable.tableHeaders.pickup")}</th>
                    <th>{t("clientTable.tableHeaders.delivery")}</th>
                    <th>{t("clientTable.tableHeaders.distance")}</th>
                    <th>{t("clientTable.tableHeaders.totalWeight")}</th>
                    <th>{t("clientTable.tableHeaders.paymentStatus")}</th>
                    <th>{t("clientTable.tableHeaders.status")}</th>
                    <th>{t("clientTable.tableHeaders.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentData().map((row, idx) => {
                    const location = getBookingLocation(row);
                    const orderId = row.order_id || row.booking_id;
                    const price = row.price || row.preis || row.sub_total;
                    const paymentStatus =
                      row.payment_status === 1 ||
                      row.payment === "Bezahlt" ||
                      row.bezahlt;

                    const unreadCount = ordersUnreadCounts[row.id] || 0;

                    // Check available data for accept/reject
                    const { transportId, orderId: extractedOrderId } =
                      getTransportAndOrderIds(row);
                    const canShowButtons = showAcceptRejectButtons(row);
                    const canShowDelete = showDeleteButton(row);

                    return (
                      <tr key={idx}>
                        <td>{row.id}</td>

                        {/* SearchFilter add search highlight to col you want to search in (order_id) */}
                        <td>
                          {(row.new_offers_count >= 1 && (activeTab === "furniture" || activeTab === "car")) && (
                            <span className="me-2">
                              <Badge bg="danger">{row.new_offers_count}</Badge>
                            </span>
                          )}

                          {searchTerm ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: orderId?.replace(
                                  new RegExp(searchTerm, "gi"),
                                  (match) =>
                                    `<mark class="bg-warning">${match}</mark>`,
                                ),
                              }}
                            />
                          ) : (
                            orderId
                          )}
                        </td>
                        <td>{location.from || row.abholung}</td>
                        <td>{location.to || row.zustellung}</td>
                        <td>{location.distance || row.strecke || "—"}</td>
                        <td>
                          {formatNumber(location.total_weight || row.total_weight)} KG
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-center gap-1">
                            {paymentStatus ? (
                              <span className="text-success fw-bold text-nowrap">
                                {t("clientTable.paymentStatus.paid")}{" "}
                                <FaCheck />
                              </span>
                            ) : (
                              <span className="text-danger fw-bold text-nowrap">
                                {t("clientTable.paymentStatus.unpaid")}{" "}
                                <FaTimes />
                              </span>
                            )}
                            {(row.invoice_status === 'canceled' || row.invoice_status === 'storniert') && (
                              <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>
                                {t("invoices.status.storniert") || "Storniert"}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(row.status)}</td>
                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            {activeTab === "booking" && (
                              <Button
                                size="sm"
                                className="action-btn-repeat"
                                onClick={() => handleRepeatBooking(row)}
                              >
                                <FaSync />
                              </Button>
                            )}

                            {(activeTab === "furniture" ||
                              activeTab === "car") && (
                                <Button
                                  size="sm"
                                  className="action-btn-repeat"
                                  onClick={() => handleRepeatTransport(row)}
                                >
                                  <FaSync />
                                </Button>
                              )}

                            <Button
                              size="sm"
                              className="action-btn-view"
                              onClick={() => {
                                let type = "booking";
                                if (activeTab === "furniture")
                                  type = "furniture";
                                if (activeTab === "car")
                                  type = "cars";
                                handleBookingDetails(row, type);
                              }}
                            >
                              {t("clientTable.buttons.view")}
                            </Button>

                            {canShowDelete && (
                              <Button
                                size="sm"
                                className="action-btn-delete"
                                onClick={() => {
                                  let type = "booking";
                                  if (activeTab === "furniture")
                                    type = "furniture";
                                  if (activeTab === "car")
                                    type = "cars";
                                  handleShowDeleteModal(row, type);
                                }}
                                title={t("clientTable.buttons.deleteOrder")}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {/* Pagination add Pagination to table */}
            {getCurrentPagination().total > 0 && (
              <div className="d-flex justify-content-center mt-4">
                <PaginationComponent
                  currentPage={getCurrentPagination().current_page}
                  lastPage={getCurrentPagination().last_page}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </div>
      </Container>

      {/* 🔸 Delete Confirmation Modal (for all) */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        className="dark-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" />
            {bookingToDelete?.type === "furniture"
              ? t("clientTable.deleteModal.deleteFurnitureTransport")
              : bookingToDelete?.type === "cars"
                ? t("clientTable.deleteModal.deleteCarTransport")
                : t("clientTable.deleteModal.deleteBooking")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteAlert.show && (
            <Alert variant={deleteAlert.variant} className="mb-3">
              {deleteAlert.message}
            </Alert>
          )}

          {!deleteAlert.show && bookingToDelete && (
            <>
              <p className="fw-bold">
                {t("clientTable.deleteModal.confirmDelete")}{" "}
                {bookingToDelete.type === "furniture"
                  ? t("clientTable.deleteModal.furnitureTransport")
                  : bookingToDelete.type === "cars"
                    ? t("clientTable.deleteModal.carTransport")
                    : t("clientTable.deleteModal.booking")}
                ?
              </p>
              <div className="mb-3 p-3 border rounded bg-light">
                <p className="mb-1">
                  <strong>{t("clientTable.deleteModal.orderId")}:</strong>{" "}
                  {bookingToDelete.order_id || bookingToDelete.booking_id}
                </p>
                <p className="mb-1">
                  <strong>{t("clientTable.deleteModal.from")}:</strong>{" "}
                  {getBookingLocation(bookingToDelete).from}
                </p>
                <p className="mb-0">
                  <strong>{t("clientTable.deleteModal.to")}:</strong>{" "}
                  {getBookingLocation(bookingToDelete).to}
                </p>
                {bookingToDelete.type !== "booking" && (
                  <p className="mb-0 text-muted">
                    <small>
                      {t("clientTable.deleteModal.type")}:{" "}
                      {bookingToDelete.type === "furniture"
                        ? t("clientTable.deleteModal.furnitureAndOthers")
                        : t("clientTable.deleteModal.carTransport")}
                    </small>
                  </p>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>
                    {t("clientTable.deleteModal.reasonForDeletion")} *
                  </strong>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={deleteReason}
                  onChange={(e) => {
                    setDeleteReason(e.target.value);
                    if (reasonError && e.target.value.trim()) {
                      setReasonError("");
                    }
                  }}
                  placeholder={t("clientTable.deleteModal.reasonPlaceholder")}
                  isInvalid={!!reasonError}
                  disabled={deleting}
                />
                <Form.Control.Feedback type="invalid">
                  {reasonError}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {t("clientTable.deleteModal.reasonHint")}
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDeleteModal}
            disabled={deleting}
          >
            {t("clientTable.buttons.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleting || !deleteReason.trim()}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("clientTable.buttons.deleting")}
              </>
            ) : (
              <>
                <FaTrash className="me-2" />
                {t("clientTable.buttons.confirmDelete")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}
